import { useMemo, useState } from 'react';

import { Combobox } from '@apps/web/components/ui/combobox';
import { CommandEmpty, CommandItem } from '@apps/web/components/ui/command';
import {
    type HHAExchangePayCodeOption,
    type HHAExchangePayCodeSelection,
} from '@libraries/typescript/api_client';

type HHAExchangePayCodeSelectorProps = {
    readonly payCodeInfo: HHAExchangePayCodeSelection | undefined;
    readonly selectedPayCodeId: number | null;
    readonly isLoading?: boolean;
    readonly error?: unknown;
    readonly disabled?: boolean;
    readonly onPayCodeChange: (payCodeId: number) => void;
};

function formatPositiveAmount(
    value: string | null | undefined,
    suffix: string,
): string | undefined {
    if (!value) {
        return undefined;
    }
    const amount = Number(value);
    if (!Number.isFinite(amount) || amount <= 0) {
        return undefined;
    }
    const rendered = amount.toFixed(2);
    return `$${rendered.endsWith('.00') ? rendered.slice(0, -3) : rendered}/${suffix}`;
}

function formatPayCode(option: HHAExchangePayCodeOption): string {
    const amounts = [
        formatPositiveAmount(option.hourly_rate, 'hr'),
        formatPositiveAmount(option.daily_rate, 'day'),
        formatPositiveAmount(option.visit_rate, 'visit'),
    ].filter((amount): amount is string => Boolean(amount));
    const name = option.discipline
        ? `${option.pay_code_name} — ${option.discipline}`
        : option.pay_code_name;
    const code = `${name} (${option.pay_code_id})`;
    return amounts.length > 0 ? `${code} · ${amounts.join(' · ')}` : code;
}

export function HHAExchangePayCodeSelector({
    payCodeInfo,
    selectedPayCodeId,
    isLoading,
    error,
    disabled,
    onPayCodeChange,
}: HHAExchangePayCodeSelectorProps) {
    const [isOpen, setIsOpen] = useState(false);
    const [search, setSearch] = useState('');
    const options = useMemo(
        () => payCodeInfo?.options ?? [],
        [payCodeInfo?.options],
    );
    const selectedOption = useMemo(
        () =>
            options.find(
                (option) => option.pay_code_id === selectedPayCodeId,
            ) ?? null,
        [selectedPayCodeId, options],
    );

    return (
        <div className="flex min-w-0 flex-1 flex-col gap-1 text-xs">
            <span className="text-muted-foreground text-xs font-medium">
                Pay Code
            </span>
            <Combobox
                open={isOpen}
                onOpenChange={setIsOpen}
                displayValue={
                    selectedOption ? formatPayCode(selectedOption) : undefined
                }
                placeholder={
                    isLoading
                        ? 'Loading pay codes…'
                        : error || options.length === 0
                          ? 'HHAeXchange default pay code'
                          : 'Select pay code'
                }
                disabled={disabled || isLoading || options.length === 0}
                searchValue={search}
                onSearchValueChange={setSearch}
                searchPlaceholder="Search pay codes..."
                triggerClassName="text-left overflow-hidden"
                contentClassName="w-80 p-1"
                contentSide="bottom"
                contentSideOffset={8}
                commandListProps={{ className: 'max-h-64' }}
            >
                <CommandEmpty className="text-muted-foreground py-6 text-center text-xs">
                    No pay codes found
                </CommandEmpty>
                {options.map((option) => {
                    const label = formatPayCode(option);
                    return (
                        <CommandItem
                            key={option.pay_code_id}
                            value={label}
                            onSelect={() => {
                                onPayCodeChange(option.pay_code_id);
                                setIsOpen(false);
                            }}
                            className="py-2 text-left text-sm"
                        >
                            {label}
                        </CommandItem>
                    );
                })}
            </Combobox>
        </div>
    );
}
